import { Handle, NodeProps, NodeToolbar, Position } from '@xyflow/react';
import { Box, Typography, IconButton, Tooltip, Link } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AppsIcon from '@mui/icons-material/Apps';
import LaunchIcon from '@mui/icons-material/Launch';
import { EntityType } from '@/components/entities/entityType';
import { AppNodeType } from './nodesUtils';

const PreviewNode = ({ data }: NodeProps<AppNodeType>) => {
  const entity = data.entity;
  const entityType = entity?.entityType;

  const isDataSource = entityType === EntityType.dataSourceInstance;
  const isUserApp    = entityType === EntityType.userAppInstance;

  const accentColor = isDataSource ? '#10b981' : isUserApp ? '#3b82f6' : '#8b5cf6';
  const Icon = isDataSource ? StorageIcon : isUserApp ? AppsIcon : SmartToyIcon;

  const defId = isDataSource
    ? (entity?.dataSourceDef?.id || entity?.dataSourceDefId || entity?.entityDefId)
    : isUserApp
    ? (entity?.userAppDef?.id || entity?.userAppDefId || entity?.entityDefId)
    : (entity?.agentDef?.id || entity?.agentDefId || entity?.entityDefId);

  let entityLink = '';
  if (isDataSource && defId && entity?.id) {
    entityLink = `/dataSourceInstance/${defId}/${entity.id}`;
  } else if (isUserApp && defId && entity?.id) {
    entityLink = `/userAppInstance/${defId}/${entity.id}`;
  } else if (defId && entity?.id) {
    entityLink = `/agentInstance/${defId}/${entity.id}`;
  }

  return (
    <>
      {entityLink && (
        <NodeToolbar isVisible={undefined} position={Position.Top} offset={4} align="end">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 0.2,
              bgcolor: 'background.paper',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}
          >
            <Tooltip title={`Open ${entity?.name || 'Details'}`}>
              <Link href={entityLink} target="_blank" underline="none">
                <IconButton
                  size="small"
                  sx={{
                    p: 0.3,
                    color: accentColor,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <LaunchIcon style={{ fontSize: '0.85rem' }} />
                </IconButton>
              </Link>
            </Tooltip>
          </Box>
        </NodeToolbar>
      )}

      <Box
        sx={{
          width: 120,
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          px: '7px',
          py: '5px',
          borderRadius: '8px',
          bgcolor: '#ffffff',
          border: '1px solid rgba(0,0,0,0.08)',
          borderLeft: `3px solid ${accentColor}`,
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
          userSelect: 'none',
          cursor: entityLink ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
            borderColor: accentColor,
          },
        }}
      >
        <Icon sx={{ fontSize: 10, color: accentColor, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: '0.6rem',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {entity?.name || '—'}
        </Typography>

        {entityLink && (
          <Tooltip title={`Open ${entity?.name || 'Details'}`}>
            <Link
              href={entityLink}
              target="_blank"
              underline="none"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <LaunchIcon
                sx={{
                  fontSize: 10,
                  color: 'text.secondary',
                  opacity: 0.6,
                  '&:hover': { opacity: 1, color: accentColor },
                }}
              />
            </Link>
          </Tooltip>
        )}

        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ width: 6, height: 6, background: accentColor, border: '1.5px solid white', left: -3, pointerEvents: 'none' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ width: 6, height: 6, background: accentColor, border: '1.5px solid white', right: -3, pointerEvents: 'none' }}
        />
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ width: 6, height: 6, background: accentColor, border: '1.5px solid white', top: -3, pointerEvents: 'none' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ width: 6, height: 6, background: accentColor, border: '1.5px solid white', bottom: -3, pointerEvents: 'none' }}
        />
      </Box>
    </>
  );
};

export default PreviewNode;
